/**
 * Freeze a subscription.
 *
 * The allowance is shown before anything is typed, and the resulting new end
 * date is shown live — a freeze changes when someone's membership runs out, so
 * the staff member should see that number before they commit, not after.
 *
 * Quick buttons cover the whole allowance and the obvious fractions of it,
 * because "the full 3 weeks" is what most people ask for.
 */
import { useState } from 'react';
import { createFreeze } from '../db/operations';
import { classLabel } from '@shared/catalog';
import { formatDisplayDate, todayISO } from '@shared/dates';
import {
  describeAllowance,
  extendEndDate,
  freezeEndDate,
  validateFreeze,
} from '@shared/freezes';
import type { SubscriptionRow } from '../db/queries';

export default function FreezeDialog({
  row,
  onClose,
}: {
  row: SubscriptionRow;
  onClose: () => void;
}) {
  const today = todayISO();
  const [startDate, setStartDate] = useState(today);
  const [days, setDays] = useState(row.freezeDaysAvailable);
  const [reason, setReason] = useState('');
  const [by, setBy] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  const errors = validateFreeze(row.subscription, row.freezes, startDate, days, today);
  const canSave = Object.keys(errors).length === 0;

  const newEndDate = extendEndDate(row.subscription.end_date, Math.max(0, days));

  const quickDays = [7, 14, 21].filter((d) => d <= row.freezeDaysAvailable);
  if (!quickDays.includes(row.freezeDaysAvailable) && row.freezeDaysAvailable > 0) {
    quickDays.push(row.freezeDaysAvailable);
  }

  async function save() {
    setTouched(true);
    if (!canSave) {
      setError(Object.values(errors)[0] ?? '');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await createFreeze({
        subscriptionId: row.subscription.id,
        startDate,
        days,
        reason,
        createdBy: by.trim() || 'Front desk',
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not freeze');
      setSaving(false);
    }
  }

  return (
    <div
      className="no-print fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-4 sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-1 text-2xl text-brand">Freeze subscription</h2>
        <p className="mb-5 text-sm text-white/50">
          {row.member.full_name} · {classLabel(row.subscription.class_key)}
        </p>

        <div className="mb-5 rounded-btn border border-edge bg-black/40 p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-white/50">Allowance</span>
            <span className="font-semibold">
              {describeAllowance(row.subscription.months)}
            </span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-white/50">Used so far</span>
            <span className="tabular font-semibold">
              {row.freezes.length} of {row.freezes.length + row.freezesLeft}
            </span>
          </div>
        </div>

        {errors.subscription ? (
          <>
            <p className="mb-5 rounded-btn border border-status-expired/40 bg-status-expiredBg p-3 text-sm text-status-expired">
              {errors.subscription}
            </p>
            <button onClick={onClose} className="btn-ghost w-full">
              Close
            </button>
          </>
        ) : (
          <>
            <label className="label" htmlFor="freezeStart">
              Starting
            </label>
            <input
              id="freezeStart"
              type="date"
              min={today}
              className="field tabular mb-4"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            {touched && errors.startDate && (
              <p className="-mt-3 mb-3 text-sm text-status-expired">{errors.startDate}</p>
            )}

            <div className="label">How many days</div>
            <div className="mb-3 flex flex-wrap gap-2">
              {quickDays.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className={`min-h-touch flex-1 rounded-btn border-2 px-3 text-sm font-bold
                              transition-all duration-brand ${
                                days === d
                                  ? 'border-brand bg-brand text-paper'
                                  : 'border-edge bg-surface hover:border-brand'
                              }`}
                >
                  {d % 7 === 0 ? `${d / 7} week${d === 7 ? '' : 's'}` : `${d} days`}
                </button>
              ))}
            </div>

            <input
              type="number"
              min={1}
              max={row.freezeDaysAvailable}
              className="field tabular mb-1"
              value={days}
              onChange={(e) => setDays(Math.trunc(Number(e.target.value) || 0))}
            />
            {touched && errors.days && (
              <p className="mb-3 text-sm text-status-expired">{errors.days}</p>
            )}

            <div className="my-4 rounded-btn border border-status-frozen/40 bg-status-frozenBg p-4">
              <div className="label mb-1">Frozen until</div>
              <div className="mb-3 text-lg font-bold text-status-frozen">
                {days > 0 ? formatDisplayDate(freezeEndDate(startDate, days)) : '—'}
              </div>
              <div className="label mb-1">Subscription now ends</div>
              <div className="text-lg font-bold">
                <span className="text-white/40 line-through">
                  {formatDisplayDate(row.subscription.end_date)}
                </span>
                <span className="mx-2 text-white/40">→</span>
                <span className="text-brand">{formatDisplayDate(newEndDate)}</span>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="label" htmlFor="freezeReason">
                  Reason
                </label>
                <input
                  id="freezeReason"
                  className="field"
                  dir="auto"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Travel, injury…"
                />
              </div>
              <div>
                <label className="label" htmlFor="freezeBy">
                  Approved by
                </label>
                <input
                  id="freezeBy"
                  className="field"
                  value={by}
                  onChange={(e) => setBy(e.target.value)}
                  placeholder="Your name"
                />
              </div>
            </div>

            {error && <p className="mt-3 text-sm text-status-expired">{error}</p>}

            <div className="mt-6 flex gap-3">
              <button onClick={onClose} className="btn-ghost flex-1">
                Cancel
              </button>
              <button onClick={save} disabled={saving} className="btn flex-1">
                {saving ? 'Freezing…' : 'Freeze'}
              </button>
            </div>

            <p className="mt-4 text-center text-xs text-white/25">
              The days are added onto the end date, so they get back exactly what they paused.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
